import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';
import { TimeRange } from '../slices/analyticsSlice';

/**
 * Fetch token purchase analytics
 */
export const fetchTokenPurchases = createAsyncThunk(
  'analytics/fetchTokenPurchases',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/token-purchases?timeRange=${timeRange}`);
      return { ...response.data, purchases: response.data.purchases };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch token purchases');
    }
  }
);

/**
 * Fetch trade analytics
 */
export const fetchTrades = createAsyncThunk(
  'analytics/fetchTrades',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/trades?timeRange=${timeRange}`);
      return { ...response.data, trades: response.data.trades };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trades');
    }
  }
);

/**
 * Fetch post analytics
 */
export const fetchPosts = createAsyncThunk(
  'analytics/fetchPosts',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/posts?timeRange=${timeRange}`);
      return { ...response.data, posts: response.data.posts };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch posts');
    }
  }
);

/**
 * Fetch message analytics
 */
export const fetchMessages = createAsyncThunk(
  'analytics/fetchMessages',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/messages?timeRange=${timeRange}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch messages');
    }
  }
);

/**
 * Fetch follower/following analytics
 */
export const fetchFollowers = createAsyncThunk(
  'analytics/fetchFollowers',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/followers?timeRange=${timeRange}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch followers');
    }
  }
);

/**
 * Fetch notification analytics
 */
export const fetchNotifications = createAsyncThunk(
  'analytics/fetchNotifications',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/notifications?timeRange=${timeRange}`);
      return { ...response.data, notifications: response.data.notifications };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch notifications');
    }
  }
);

/**
 * Fetch engagement analytics
 */
export const fetchEngagement = createAsyncThunk(
  'analytics/fetchEngagement',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/engagement?timeRange=${timeRange}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch engagement');
    }
  }
);

/**
 * Fetch content earnings analytics (PPV sales, view fees, subscriptions)
 */
export const fetchContentEarnings = createAsyncThunk(
  'analytics/fetchContentEarnings',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/content-earnings?timeRange=${timeRange}`);
      return { ...response.data, timeRange };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch content earnings');
    }
  }
);

/**
 * Fetch profile view analytics
 */
export const fetchProfileViews = createAsyncThunk(
  'analytics/fetchProfileViews',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/profile-views?timeRange=${timeRange}`);
      return { ...response.data, timeRange };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch profile views');
    }
  }
);

/**
 * Fetch share-link click analytics (#256)
 */
export const fetchShareClicks = createAsyncThunk(
  'analytics/fetchShareClicks',
  async (timeRange: TimeRange = '30d', { rejectWithValue }) => {
    try {
      const response = await api.get(`/analytics/share-clicks?timeRange=${timeRange}`);
      return { ...response.data, timeRange };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch share clicks');
    }
  }
);

/**
 * Fetch royalties analytics
 */
export const fetchRoyalties = createAsyncThunk(
  'analytics/fetchRoyalties',
  async (
    params: {
      timeRange: TimeRange;
      type?: 'all' | 'profile_sales' | 'spotlights' | 'dm_spotlight' | 'post_spotlight' | 'profile_spotlight';
    },
    { rejectWithValue }
  ) => {
    try {
      const { timeRange = '30d', type = 'all' } = params;
      const response = await api.get(`/analytics/royalties?timeRange=${timeRange}&type=${type}`);
      return { ...response.data, timeRange };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch royalties');
    }
  }
);

/**
 * Fetch the logged-in creator's public reputation snapshot for the
 * Reputation tab on the analytics page (ADR-008 option B wedge
 * surfacing). Auth-scoped wrapper over the same scoring pipeline that
 * drives the public /api/public/reputation/:profileId endpoint.
 *
 * Server returns { hasProfile: false } when the caller hasn't minted
 * an NFT profile yet; the thunk resolves to `null` in that case so the
 * reducer / component can render an empty-state.
 */
export const fetchReputation = createAsyncThunk(
  'analytics/fetchReputation',
  async (_arg: void, { rejectWithValue }) => {
    try {
      const response = await api.get('/analytics/my-reputation');
      return response.data.hasProfile ? response.data.reputation : null;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch reputation',
      );
    }
  },
);
